import { calcPMT } from "./cet.js";
import { isContratoTelemedicina } from "./contrato.js";
/**
 * TETO DE CET NO EMPRESTIMO — spec 014 (decisao do Caio, 29/09/2026).
 *
 * A tabela de emprestimo do banco publica o CET a.m. (nao os juros), e o teto
 * que a prefeitura define no convenio limita o CET. Motivo: teto de juros e'
 * contornavel — o banco publica juros baixo e recupera em tarifa, seguro e
 * encargo. O CET inclui tudo isso por definicao; travando o CET, os juros ficam
 * limitados por consequencia (o CET nunca e' menor que os juros).
 *
 * A trava vale so para emprestimo, portabilidade e refinanciamento. Cartao de
 * credito consignado e cartao beneficio tem taxas de outra ordem e nunca passam
 * por ela.
 *
 * COMO A PARCELA SAI DO CET
 * -------------------------
 * CET e' a taxa que iguala o que o servidor RECEBE ao fluxo de parcelas que ele
 * PAGA. Com o CET em maos, a parcela e' a Price sobre o valor liquido — sem IOF,
 * tarifa ou seguro somados a parte, porque ja estao dentro dele. Somar o IOF de
 * novo (o que `calcCET` faz, porque parte de juros) contaria o imposto duas
 * vezes: e' o defeito que a TBL-16-101 do BB mostrava, 1,79% de CET publicado e
 * 1,88% a 2,12% exibido ao servidor.
 */
/**
 * Menor CET a.m. aceito. Protege contra erro de digitacao — "1,79" que vira
 * "0,179" publica um emprestimo a 0,18% ao mes. Decisao 9 da spec 014; ajustavel.
 */
export const CET_MINIMO_AM = 0.005;
/**
 * Produto sujeito ao teto de CET do convenio? Cartao nunca; telemedicina
 * tambem nao (e' mensalidade com taxa 0, nao credito).
 *
 * `tipoMargem` e' a fonte quando existe; dado antigo so tem `tipoContrato`, onde
 * ECONSIGNADO significa cartao — a mesma derivacao de `deriveTipoMargem`.
 */
export function produtoSujeitoAoTetoDeCet(ct) {
    if (isContratoTelemedicina({ tipoContrato: ct.tipoContrato ?? undefined, observacoes: ct.observacoes }))
        return false;
    if (ct.tipoMargem)
        return !ct.tipoMargem.toUpperCase().startsWith("CARTAO");
    return (ct.tipoContrato ?? "").toUpperCase() !== "ECONSIGNADO";
}
/** CET anual equivalente ao mensal (composto). */
export function cetAnual(cetAm) {
    return Math.pow(1 + cetAm, 12) - 1;
}
/** Parcela Price sobre o liquido, na taxa do CET (com carencia, se houver). */
export function parcelaPeloCet({ valorLiquido, parcelas, cetAm, diasCarencia = 30 }) {
    if (!(valorLiquido > 0) || !(parcelas > 0))
        return 0;
    const corrigido = diasCarencia > 30 ? valorLiquido * Math.pow(1 + cetAm, (diasCarencia - 30) / 30) : valorLiquido;
    return calcPMT(corrigido, parcelas, cetAm);
}
/** Parcela, total e CET anual — o que a tela e o contrato mostram. */
export function condicoesPeloCet(input) {
    const parcela = parcelaPeloCet(input);
    return { parcela, totalPago: parcela * input.parcelas, cetAm: input.cetAm, cetAa: cetAnual(input.cetAm) };
}
/**
 * Inverso: maior liquido cuja parcela cabe na margem, no CET dado.
 * Responde "quanto eu consigo?" quando o valor pedido nao passa.
 */
export function liquidoMaximoPeloCet(margem, parcelas, cetAm, diasCarencia = 30) {
    if (!(margem > 0) || !(parcelas > 0))
        return 0;
    const principal = cetAm > 0 ? (margem * (1 - Math.pow(1 + cetAm, -parcelas))) / cetAm : margem * parcelas;
    return diasCarencia > 30 ? principal / Math.pow(1 + cetAm, (diasCarencia - 30) / 30) : principal;
}
/** Teto de CET do convenio: `taxaMaxAm` (PERCENTUAL) em fracao, ou null. */
export function tetoDeCetDoConvenio(cfg) {
    const pct = cfg?.taxaMaxAm;
    return pct == null || !Number.isFinite(pct) || pct <= 0 ? null : pct / 100;
}
const pct = (f) => `${(f * 100).toFixed(2).replace(".", ",")}%`;
/**
 * Confere um CET contra as regras da spec 014, na ordem em que o operador
 * precisa ler: minimo (erro de digitacao), teto da prefeitura, preco publicado
 * na tabela, coerencia com os juros. Devolve o primeiro motivo, ou null.
 *
 * NAO decide se o produto e' sujeito: o chamador so' chama para quem
 * `produtoSujeitoAoTetoDeCet` — cartao nunca chega aqui.
 */
export function conferirCet(args) {
    const { cetAm, teto, cetDaTabela, jurosAm, convenioNome } = args;
    const noConvenio = convenioNome ? ` para o convenio ${convenioNome}` : " para este convenio";
    if (!(cetAm >= CET_MINIMO_AM - 1e-9)) {
        return {
            codigo: "cet_abaixo_do_minimo",
            mensagem: `CET de ${pct(cetAm)} a.m. abaixo do minimo de ${pct(CET_MINIMO_AM)}. Confira a digitacao: o CET e' informado em percentual ao mes (1,79 para 1,79%).`,
        };
    }
    if (teto != null && cetAm > teto + 1e-9) {
        return {
            codigo: "cet_acima_do_teto",
            mensagem: `CET de ${pct(cetAm)} a.m. excede o teto de ${pct(teto)} definido pela prefeitura${noConvenio}.`,
        };
    }
    if (cetDaTabela != null && cetDaTabela > 0 && cetAm > cetDaTabela + 1e-9) {
        return {
            codigo: "cet_acima_da_tabela",
            mensagem: `CET de ${pct(cetAm)} a.m. acima do publicado na tabela do banco para este prazo (${pct(cetDaTabela)}). Abaixo da tabela o banco negocia livremente; acima, nao.`,
        };
    }
    if (jurosAm != null && jurosAm > 0 && cetAm < jurosAm - 1e-9) {
        return {
            codigo: "cet_abaixo_dos_juros",
            mensagem: `CET de ${pct(cetAm)} a.m. menor que os juros de ${pct(jurosAm)} a.m. O CET inclui os juros e nunca fica abaixo deles — confira se os dois campos nao foram trocados.`,
        };
    }
    return null;
}
/**
 * Faixa de prazo da nova tabela cruza a de outra tabela ATIVA do mesmo
 * convenio? A vigencia da tabela e' a do convenio (decisao de 11/08/2026), entao
 * duas tabelas ativas do mesmo convenio sempre tem vigencias que se cruzam — a
 * conferencia fica so no prazo. Decisao 10 da spec 014: antes, a sobreposicao
 * valia pela menor taxa sem aviso nenhum.
 *
 * @returns a primeira tabela que cruza, ou null.
 */
export function tabelaQueSobrepoe(outras, nova) {
    const min = (t) => (t.prazoMinMeses != null && t.prazoMinMeses > 0 ? t.prazoMinMeses : 1);
    const a0 = min(nova);
    const a1 = nova.prazoMaxMeses;
    for (const t of outras) {
        if (nova.id && t.id === nova.id)
            continue;
        if (a0 <= t.prazoMaxMeses && min(t) <= a1)
            return t;
    }
    return null;
}
//# sourceMappingURL=teto-cet.js.map